import React, { useState, useEffect } from 'react';
import {
    X, ChevronLeft, ChevronRight, Star, User, Calendar,
    CheckCircle2, Clock, Loader2, Mail, Briefcase, MapPin,
    MessageSquare, ThumbsUp, ThumbsDown
} from 'lucide-react';
import api from '../../../utils/api';
import toast from 'react-hot-toast';

const ResponseDetailModal = ({ isOpen, onClose, surveyId, instanceId, onNavigate }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isOpen && surveyId && instanceId) {
            fetchDetail();
        }
    }, [isOpen, surveyId, instanceId]);

    const fetchDetail = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/surveys/${surveyId}/analytics/individual/${instanceId}`);
            setData(response.data);
        } catch (error) {
            console.error('Error fetching response detail:', error);
            toast.error('Failed to load response details');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('en-US', {
            weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const renderAnswer = (response) => {
        if (response.skipped) {
            return (
                <div className="px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="text-sm text-gray-400 italic">Skipped</span>
                </div>
            );
        }

        switch (response.question_type) {
            case 'rating': {
                const val = response.response_value || parseInt(response.response_text);
                return (
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map(i => (
                                <Star
                                    key={i}
                                    size={20}
                                    className={i <= val ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}
                                />
                            ))}
                        </div>
                        <span className="text-lg font-bold text-gray-800">{val}</span>
                        <span className="text-sm text-gray-400">out of 5</span>
                    </div>
                );
            }
            case 'boolean': {
                const answer = (response.response_text || '').toLowerCase();
                const isYes = ['yes', 'true', '1'].includes(answer);
                return (
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${isYes
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                        {isYes ? <ThumbsUp size={16} /> : <ThumbsDown size={16} />}
                        <span className="font-medium">{isYes ? 'Yes' : 'No'}</span>
                    </div>
                );
            }
            case 'multiple_choice': {
                return (
                    <div className="px-4 py-2.5 bg-indigo-50 rounded-lg border border-indigo-100">
                        <span className="text-sm font-medium text-indigo-700">{response.response_text}</span>
                    </div>
                );
            }
            case 'text':
            default: {
                return (
                    <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{response.response_text}</p>
                        {response.sentiment_label && (
                            <div className="mt-2 flex items-center gap-2">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${response.sentiment_label === 'positive' ? 'bg-green-100 text-green-700' :
                                    response.sentiment_label === 'negative' ? 'bg-red-100 text-red-700' :
                                        'bg-gray-100 text-gray-600'
                                    }`}>
                                    {response.sentiment_label}
                                </span>
                            </div>
                        )}
                    </div>
                );
            }
        }
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="fixed inset-y-0 right-0 z-[70] w-full max-w-xl bg-white shadow-2xl flex flex-col animate-slide-in-right">
                {/* Header */}
                <div className="px-6 py-4 bg-white border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X size={18} />
                        </button>
                        <div>
                            <h3 className="text-base font-bold text-gray-900">Response Details</h3>
                            {data && (
                                <p className="text-xs text-gray-500">
                                    {data.navigation.current_index} of {data.navigation.total} responses
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Navigation */}
                    {data?.navigation && (
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => data.navigation.prev_instance_id && onNavigate?.(data.navigation.prev_instance_id)}
                                disabled={!data.navigation.prev_instance_id}
                                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                title="Previous response"
                            >
                                <ChevronLeft size={18} />
                            </button>
                            <button
                                onClick={() => data.navigation.next_instance_id && onNavigate?.(data.navigation.next_instance_id)}
                                disabled={!data.navigation.next_instance_id}
                                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                title="Next response"
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
                    {loading ? (
                        <div className="flex items-center justify-center h-48">
                            <Loader2 size={28} className="animate-spin text-indigo-600" />
                        </div>
                    ) : !data ? (
                        <div className="text-center py-12 text-gray-500">Response not found</div>
                    ) : (
                        <div className="space-y-6">
                            {/* Employee Info */}
                            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
                                        {data.is_anonymous ? '?' : (data.employee_name?.charAt(0) || '?')}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-lg font-bold text-gray-900">{data.employee_name}</h4>
                                        {!data.is_anonymous && (
                                            <div className="mt-1 space-y-1">
                                                {data.email && (
                                                    <p className="text-sm text-gray-500 flex items-center gap-1.5">
                                                        <Mail size={13} className="text-gray-400" />
                                                        {data.email}
                                                    </p>
                                                )}
                                                {data.position && (
                                                    <p className="text-sm text-gray-500 flex items-center gap-1.5">
                                                        <Briefcase size={13} className="text-gray-400" />
                                                        {data.position}
                                                    </p>
                                                )}
                                                {data.department && (
                                                    <p className="text-sm text-gray-500 flex items-center gap-1.5">
                                                        <MapPin size={13} className="text-gray-400" />
                                                        {data.department}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${data.status === 'completed' ? 'bg-green-50 text-green-700' :
                                        data.status === 'in_progress' ? 'bg-blue-50 text-blue-700' :
                                            'bg-gray-50 text-gray-600'
                                        }`}>
                                        {data.status?.replace('_', ' ')}
                                    </span>
                                </div>

                                {/* Survey info + dates */}
                                <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Survey</p>
                                        <p className="text-sm text-gray-800 font-medium mt-0.5">{data.survey_title}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Completed</p>
                                        <p className="text-sm text-gray-800 mt-0.5">{formatDate(data.completed_at)}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Responses */}
                            <div className="space-y-4">
                                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                                    <MessageSquare size={14} className="text-indigo-500" />
                                    Responses ({data.responses?.length || 0})
                                </h4>

                                {data.responses?.map((r, idx) => (
                                    <div key={idx} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                                        <div className="flex items-start justify-between mb-3">
                                            <div>
                                                <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-indigo-50 text-indigo-600 mb-1.5">
                                                    {r.question_type}
                                                </span>
                                                <p className="text-sm font-medium text-gray-800 leading-relaxed">{r.question_text}</p>
                                            </div>
                                            <span className="text-xs text-gray-400 flex-shrink-0 ml-3">Q{idx + 1}</span>
                                        </div>
                                        <div className="mt-2">
                                            {renderAnswer(r)}
                                        </div>
                                    </div>
                                ))}

                                {(!data.responses || data.responses.length === 0) && (
                                    <div className="text-center py-8 text-gray-400">
                                        <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">No responses recorded</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes slide-in-right {
                    from { transform: translateX(100%); }
                    to { transform: translateX(0); }
                }
                .animate-slide-in-right {
                    animation: slide-in-right 0.25s ease-out;
                }
            `}</style>
        </>
    );
};

export default ResponseDetailModal;
